<template>
  <transition name="edit-question-modal" appear>
    <div
      v-if="dialog"
      class="edit-question-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-question-modal-title"
      @mousedown.self="onClose"
    >
      <div class="edit-question-modal__card">
        <div class="edit-question-modal__content">
          <div class="edit-question-modal__header">
            <div class="edit-question-modal__header-icon">
              <Icon :name="'quiz_icon'" />
            </div>
            <button
              type="button"
              class="edit-question-modal__close-btn"
              aria-label="Close"
              :disabled="isSubmitting"
              @click="onClose"
            >
              <Icon name="popup_close" />
            </button>
          </div>
          <div class="edit-question-modal__header-text">
            <span
              id="edit-question-modal-title"
              class="edit-question-modal__title"
            >
              Edit Quiz Question
            </span>
            <span class="edit-question-modal__description">
              Here you can edit the question and answers
            </span>
          </div>

          <div class="edit-question-modal__content-body" :key="formKey">
            <EditQuestionHeader
              v-model="question"
              placeholder="Enter a question"
              :setFieldDirty="true"
              :original-value="initialQuestion"
              :model-value="initialQuestion"
            />

            <AnswersEditBlock
              :ordered_list_of_answers="orderedAnswers"
              :max-answers="MAX_QUIZ_ANSWERS"
              :original_ordered_list_of_answers="initialAnswers"
              @update:ordered_list_of_answers="handleAnswersUpdate"
              :setFieldDirty="true"
              :showDefaultHover="true"
            />
          </div>
        </div>

        <div class="ex-propose-kit-changes__footer d-flex mt-2">
          <div class="ex-propose-kit-changes__footer-btn">
            <StandardButton
              type="magenta"
              variant="outline"
              size="full-width"
              label="Discard"
              :disabled="isSubmitting"
              @click="onDiscard"
            />
          </div>
          <div class="ex-propose-kit-changes__footer-btn">
            <StandardButton
              type="magenta"
              variant="primary"
              size="full-width"
              label="Save"
              :disabled="!isSaveEnabled || isSubmitting"
              :loaderLeft="isSubmitting"
              @click="onSave"
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import EditQuestionHeader from "@components/EditQuestionHeader/EditQuestionHeader.vue";
import AnswersEditBlock from "@components/AnswersEditBlock/AnswersEditBlock.vue";
import { MIN_QUIZ_ANSWERS, MAX_QUIZ_ANSWERS } from "@components/constants/quizLimits";

const props = defineProps({
  dialog: {
    type: Boolean,
    default: false,
  },
  iconName: {
    type: String,
    default: "quiz_icon",
  },
  initialQuestion: {
    type: String,
    default: "",
  },
  initialAnswers: {
    type: Array,
    default: () => [
      { answer: "", checked: false, sequence: 1 },
      { answer: "", checked: false, sequence: 2 },
      { answer: "", checked: false, sequence: 3 },
      { answer: "", checked: false, sequence: 4 },
    ],
  },
  /** When true: Save shows loader; dismiss controls disabled; overlay click does not close. */
  isSubmitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update:modelValue",
  "input",
  "close",
  "discard",
  "save",
]);

const question = ref(props.initialQuestion);
const orderedAnswers = ref([...props.initialAnswers]);
const formKey = ref(0);

const hasSelectedAnswer = computed(() =>
  orderedAnswers.value.some((a) => a.checked)
);

const isQuestionDirty = computed(
  () => (question.value || "") !== (props.initialQuestion || "")
);

// When you change an answer → show field dirty, enable Save
const anyAnswerDirty = computed(() => {
  const current = orderedAnswers.value;
  const original = props.initialAnswers || [];
  return current.some((ans, i) => {
    const orig = original[i];
    if (!orig) {
      return (ans.answer || "").trim() !== "";
    }
    return (ans.answer || "") !== (orig.answer || "");
  });
});

// When you change a selection (and there is at least one selection) → enable Save
const isSelectionChanged = computed(() => {
  const current = orderedAnswers.value;
  const original = props.initialAnswers || [];
  if (current.length !== original.length) return true;
  return current.some((ans, i) => {
    const orig = original[i];
    return orig && (ans.checked !== orig.checked);
  });
});

// When you delete an answer → enable Save
const answersDeleted = computed(
  () => orderedAnswers.value.length < (props.initialAnswers?.length || 0)
);

// When you add a valid answer → enable Save
const addedValidAnswer = computed(() => {
  const current = orderedAnswers.value;
  const initialLen = props.initialAnswers?.length || 0;
  if (current.length <= initialLen) return false;
  return current
    .slice(initialLen)
    .some((ans) => (ans.answer || "").trim() !== "");
});

const hasChanges = computed(
  () =>
    isQuestionDirty.value ||
    anyAnswerDirty.value ||
    (isSelectionChanged.value && hasSelectedAnswer.value) ||
    answersDeleted.value ||
    addedValidAnswer.value
);

// No empty fields: question and all answers must have content; at least one selected
const hasValidForm = computed(() => {
  const q = (question.value || "").trim();
  if (q === "") return false;
  if (!hasSelectedAnswer.value) return false;
  const answers = orderedAnswers.value;
  if (answers.length < MIN_QUIZ_ANSWERS || answers.length > MAX_QUIZ_ANSWERS) return false;
  return answers.every((a) => (a.answer || "").trim() !== "");
});

const isSaveEnabled = computed(
  () => hasChanges.value && hasValidForm.value
);

function onClose() {
  if (props.isSubmitting) return;
  emit("update:modelValue", false);
  emit("input", false);
  emit("close");
}

function onDiscard() {
  if (props.isSubmitting) return;
  resetState();
  onClose();
  emit("discard");
}

function onSave() {
  if (!isSaveEnabled.value || props.isSubmitting) return;
  emit("save", {
    question: question.value,
    answers: orderedAnswers.value,
  });
}

function handleAnswersUpdate(newAnswers) {
  orderedAnswers.value = newAnswers;
}

function resetState() {
  question.value = props.initialQuestion;
  orderedAnswers.value = [...props.initialAnswers];
  formKey.value += 1;
}

watch(
  () => props.dialog,
  (val) => {
    if (val) {
      question.value = props.initialQuestion;
      orderedAnswers.value = [...props.initialAnswers];
    }
  }
);
</script>
<style scoped>
.edit-question-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
}

.edit-question-modal__card {
  background-image: url("@components/assets/backgrounds/background_popup_icon.svg");
  background-size: 216px 216px;
  background-position: 0px 0px;
  width: 826px;
  max-height: 95vh;
  border-radius: 12px;
  border: 1px solid #eaecf0;
  box-shadow: 0 20px 24px -4px rgba(16, 24, 40, 0.08),
    0 8px 8px -4px rgba(16, 24, 40, 0.03);
  position: relative;
  background-color: #ffffff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.edit-question-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 20px 0 20px;
}

.edit-question-modal__header-icon {
  display: flex;
  width: 48px;
  height: 48px;
  padding: 12px;
  justify-content: center;
  align-items: center;
  border-radius: var(--radius-lg, 10px);
  border: 1px solid #eaecf0;
  background: var(--White, #fff);
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  flex: 0 0 auto;
  line-height: 0;
  box-sizing: border-box;
}

.edit-question-modal__header-icon > * {
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
}

.edit-question-modal__header-icon svg {
  display: block;
  flex-shrink: 0;
}

.edit-question-modal__close-btn {
  appearance: none;
  background: transparent;
  border: 0;
  padding: 6px;
  margin: 0;
  cursor: pointer;
  color: #98a2b3;
  font-size: 26px;
  font-weight: 400;
  line-height: 1;
}

.edit-question-modal__close-btn:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
  border-radius: 8px;
}

.edit-question-modal__content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 6px 0 20px 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 16px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.edit-question-modal__content::-webkit-scrollbar {
  display: none;
}

.edit-question-modal__header-text {
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  padding: 0px 20px;
}

.edit-question-modal__title {
  color: var(--Big-Headings, #101828);
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 28px;
}

.edit-question-modal__description {
  color: #666;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}
.ex-propose-kit-changes__footer {
  gap: 12px;
  padding: 0px 20px 20px 20px;
  width: 100%;
}
.ex-propose-kit-changes__footer-btn {
  flex: 1;
  min-width: 0;
}

/* Transition */
.edit-question-modal-enter-active,
.edit-question-modal-leave-active {
  transition: opacity 180ms ease;
}

.edit-question-modal-enter-from,
.edit-question-modal-leave-to {
  opacity: 0;
}

.edit-question-modal-enter-active .edit-question-modal__card,
.edit-question-modal-leave-active .edit-question-modal__card {
  transition: transform 200ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease;
  will-change: transform, opacity;
}

.edit-question-modal-enter-from .edit-question-modal__card,
.edit-question-modal-leave-to .edit-question-modal__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}
::v-deep(.header-message--answer) {
  right: 34px !important;
}
</style>
